import type { SupportedCountry } from "./types";

/** Display metadata for the currencies we currently support. Falls back to the
 *  raw ISO code for anything not listed, so new backend currencies still render. */
export const CURRENCY_META: Record<string, { name: string; symbol: string }> = {
  NPR: { name: "Nepalese Rupee", symbol: "Rs." },
  USD: { name: "US Dollar", symbol: "$" },
  INR: { name: "Indian Rupee", symbol: "₹" },
};

export function getCurrencyLabel(code: string): string {
  const meta = CURRENCY_META[code];
  return meta ? `${code} — ${meta.name}` : code;
}

function isValidCountry(c: unknown): c is SupportedCountry {
  return (
    typeof c === "object" &&
    c !== null &&
    typeof (c as { id?: unknown }).id === "string"
  );
}

/** GET /merchant/countries returns a bare array; tolerate a { data: [] } envelope too. */
export function normalizeCountries(res: unknown): SupportedCountry[] {
  const arr = Array.isArray(res)
    ? res
    : Array.isArray((res as { data?: unknown } | null)?.data)
      ? ((res as { data: unknown[] }).data)
      : [];
  return arr.filter(isValidCountry);
}

/** Distinct currency codes across the supported countries, as select options. */
export function getSupportedCurrencyOptions(
  countries: SupportedCountry[],
): { value: string; label: string }[] {
  const seen = new Set<string>();
  const codes: string[] = [];
  for (const c of countries) {
    if (c.currencyCode && !seen.has(c.currencyCode)) {
      seen.add(c.currencyCode);
      codes.push(c.currencyCode);
    }
  }
  codes.sort();
  return codes.map((value) => ({ value, label: getCurrencyLabel(value) }));
}

export function resolveDefaultCurrency(
  countries: SupportedCountry[],
  countryId: string | undefined,
): string {
  if (!countryId) return "";
  return countries.find((c) => c.id === countryId)?.currencyCode ?? "";
}

export function findCountryName(
  countries: SupportedCountry[],
  countryId: string | undefined,
): string | null {
  if (!countryId) return null;
  return countries.find((c) => c.id === countryId)?.name ?? null;
}

/** Resolve the ISO country code (e.g. "US") for a selected country id — used
 *  to fetch country-specific backend schemas like the business-info form. */
export function findCountryCode(
  countries: SupportedCountry[],
  countryId: string | undefined,
): string | null {
  if (!countryId) return null;
  return countries.find((c) => c.id === countryId)?.countryCode ?? null;
}
